import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../../../../shared/hooks';
import type {
  EventStep,
  Finding,
  LogEntry,
} from '../../data-layer/insightDashboardTypes';
import {
  formatTickLabel,
  formatTimestamp,
  getLogEntriesByLines,
  getPhaseShortLabel,
  getSessionIdsForFinding,
  parseFullTimestamp,
} from '../../data-layer/insightDashboardUtils';

import { RiskGauge, SeverityBadge } from './FindingCard';

type ModalTab = 'sequence' | 'evidence';

interface FindingDetailModalProps {
  finding: Finding;
  logEntries: LogEntry[];
  onClose: () => void;
  isInline?: boolean;
  sessionColorMap?: Record<string, string> | null;
}

const defaultProps: Partial<FindingDetailModalProps> = {
  isInline: undefined,
  sessionColorMap: undefined,
};

const RIGHT_ARROW = '\u2192';
const SESSION_COLON = 'Session:';
const SESSION_ACTIVITY_PAREN = 'Session Activity (';
const EN_DASH = '\u2013';
const CLOSE_PAREN = ')';
const S_PREFIX = 'S-';
const CROSS_SESSION = 'Cross-Session';
const TIMES_SYMBOL = '\u00D7';
const ELAPSED_LABEL = 'Elapsed:';
const SEQUENCE_TIMELINE = 'Sequence Timeline';
const EVIDENCE_LABEL = 'Evidence';
const NO_EVIDENCE_MSG = 'No matching log entries found for evidence lines.';
const HASH_PREFIX = '#';
const SESSION_TH = 'Session';
const TCODE_TH = 'T-Code';
const LOG_TYPE_TH = 'Log Type';
const START_UTC_TH = 'Start (UTC)';
const END_UTC_TH = 'End (UTC)';
const PROGRAM_TH = 'Program';
const DETAILS_TH = 'Details';
const EM_DASH = '\u2014';
const IMPACT_LABEL = 'Impact';
const RECOMMENDATION_LABEL = 'Recommendation';

function computeElapsedTime(steps: EventStep[]): string {
  if (steps.length < 2) {
    return '--';
  }

  const first = steps[0].timestamp;
  const last = steps[steps.length - 1].timestamp;

  if (!first || !last) {
    return '--';
  }

  const parseTime = (ts: string): number => {
    const parts = ts.split(':').map(Number);

    if (parts.length < 2) {
      return 0;
    }

    const hours = parts[0];
    const minutes = parts[1];
    const seconds = parts.length > 2 ? parts[2] : 0;

    return hours * 3600 + minutes * 60 + seconds;
  };

  const diff = parseTime(last) - parseTime(first);

  if (diff <= 0) {
    return '--';
  }

  if (diff < 60) {
    return `${diff}s`;
  }

  const mins = Math.floor(diff / 60);
  const secs = diff % 60;

  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

/* eslint-disable react/require-default-props */
function StepCard({
  step,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  isLast: _isLast,
  prevSessionId,
  onSessionClick,
}: {
  step: EventStep;
  isLast?: boolean;
  prevSessionId?: string;
  onSessionClick?: (sessionId: string) => void;
}) {
  const l = useInvertedLocalize();
  const showSessionSwitch =
    prevSessionId !== undefined &&
    step.sessionId !== undefined &&
    step.sessionId !== prevSessionId;

  return (
    <Box className='Sod-FindingDetail-stepCol'>
      <Box className='Sod-FindingDetail-stepCard'>
        {showSessionSwitch && (
          <Box className='Sod-FindingDetail-sessionSwitch'>
            <Typography
              kind='label'
              className='Sod-FindingDetail-sessionSwitch-text'>
              {prevSessionId} {RIGHT_ARROW} {step.sessionId}
            </Typography>
          </Box>
        )}
        <Box className='Sod-FindingDetail-stepCard-header'>
          <Typography kind='label' className='Sod-FindingDetail-stepCard-time'>
            {step.timestamp ?? '--'}
          </Typography>
        </Box>
        <Typography kind='body1' className='Sod-FindingDetail-stepCard-action'>
          {step.action}
        </Typography>
        <Typography kind='label' className='Sod-FindingDetail-stepCard-detail'>
          {step.detail ?? '\u00A0'}
        </Typography>
        {!step.sessionId && (
          <Typography
            kind='label'
            className='Sod-FindingDetail-stepCard-session'>
            {'\u00A0'}
          </Typography>
        )}
        {step.sessionId && !onSessionClick && (
          <Typography
            kind='label'
            className='Sod-FindingDetail-stepCard-session'>
            {SESSION_COLON} {step.sessionId}
          </Typography>
        )}
        {step.sessionId && onSessionClick && (
          <button
            type='button'
            className={cx(
              'Sod-FindingDetail-stepCard-session',
              'Sod-InsightTimeline-sessionLink'
            )}
            aria-label={l('Open session {sessionId}', {
              sessionId: step.sessionId,
            })}
            onClick={() => {
              if (step.sessionId) {
                onSessionClick(step.sessionId);
              }
            }}>
            {SESSION_COLON} {step.sessionId}
          </button>
        )}
      </Box>
    </Box>
  );
}

/**
 * @description Modal or inline panel displaying full details for a single finding,
 * including a sequence timeline, evidence log entries (rendered as a semantic table
 * with columns for session, T-code, log type, start/end UTC, program, and details),
 * and impact/recommendation text. Status actions (reviewed, escalated, dismissed)
 * are handled by the parent FindingCard. When rendered inline, the header (title,
 * badges, risk gauge) is omitted since the parent card already surfaces that information.
 *
 * @param props - Component props.
 * @param props.finding - The finding to display.
 * @param props.logEntries - All log entries used to resolve session IDs and evidence lines.
 * @param props.onClose - Callback invoked when the modal/panel is closed.
 * @param props.isInline - When true, renders as an inline panel beneath the finding card
 *   and omits the header. When false (default), renders as a centered overlay modal.
 * @returns The rendered finding detail modal or inline panel.
 */
/* eslint-disable react/require-default-props */
/**
 * Mini stacked area chart for the finding's time window (used inside finding detail views).
 */
function MiniTimelineChart({
  finding,
  logEntries,
  sessionColorMap,
}: {
  finding: Finding;
  logEntries: LogEntry[];
  sessionColorMap?: Record<string, string> | null;
}) {
  const chartData = useMemo(() => {
    // Get time range from finding steps
    const timestamps: number[] = [];

    finding.steps.forEach((step) => {
      if (!step.timestamp || !step.sessionId) {
        return;
      }

      // Find a reference log entry to get the base date
      const refEntry = logEntries.find(
        (e) =>
          e.sessionId === step.sessionId &&
          parseFullTimestamp(e.startTimeUtc) > 0
      );

      if (!refEntry) {
        return;
      }

      const base = parseFullTimestamp(refEntry.startTimeUtc);
      const baseDate = new Date(base * 1000);
      const dayStart =
        new Date(
          baseDate.getFullYear(),
          baseDate.getMonth(),
          baseDate.getDate()
        ).getTime() / 1000;
      const parts = step.timestamp.split(':');
      const epoch =
        dayStart +
        parseInt(parts[0] ?? '0', 10) * 3600 +
        parseInt(parts[1] ?? '0', 10) * 60 +
        parseInt(parts[2] ?? '0', 10);

      timestamps.push(epoch);
    });

    if (timestamps.length === 0) {
      return null;
    }

    const minTime = Math.min(...timestamps) - 60; // 1 min padding before
    const maxTime = Math.max(...timestamps) + 60; // 1 min padding after
    const bucketSize = Math.max(10, Math.floor((maxTime - minTime) / 20)); // ~20 buckets
    const bucketCount = Math.ceil((maxTime - minTime) / bucketSize);

    // Collect sessions involved
    const sessionIds = new Set<string>();

    finding.steps.forEach((step) => {
      if (step.sessionId) {
        sessionIds.add(step.sessionId);
      }
    });

    // Build buckets per session
    const sessionArr = Array.from(sessionIds).sort();
    const buckets: {
      epoch: number;
      label: string;
      sessions: Record<string, number>;
    }[] = [];

    Array.from({ length: bucketCount }).forEach((_, i) => {
      const epoch = minTime + i * bucketSize;
      const sessions: Record<string, number> = {};

      sessionArr.forEach((sid) => {
        sessions[sid] = 0;
      });

      buckets.push({
        epoch,
        label: formatTickLabel(epoch, (maxTime - minTime) / 60),
        sessions,
      });
    });

    // Fill buckets from log entries in the time window
    logEntries.forEach((entry) => {
      if (!sessionIds.has(entry.sessionId)) {
        return;
      }

      const t = parseFullTimestamp(entry.logCreationDateTime);

      if (t < minTime || t > maxTime) {
        return;
      }

      const idx = Math.min(
        bucketCount - 1,
        Math.floor((t - minTime) / bucketSize)
      );

      buckets[idx].sessions[entry.sessionId] += 1;
    });

    // Find max stacked value
    let maxY = 1;

    buckets.forEach((b) => {
      let total = 0;

      sessionArr.forEach((sid) => {
        total += b.sessions[sid];
      });

      if (total > maxY) {
        maxY = total;
      }
    });

    // Step marker positions (epoch + session)
    const stepMarkers = timestamps.map((epoch, i) => ({
      epoch,
      sessionId: finding.steps[i]?.sessionId ?? '',
      label: finding.steps[i]?.action ?? '',
    }));

    return { buckets, sessionArr, maxY, minTime, maxTime, stepMarkers };
  }, [finding, logEntries]);

  if (!chartData || chartData.buckets.length < 2) {
    return null;
  }

  const { buckets, sessionArr, maxY, minTime, maxTime, stepMarkers } =
    chartData;
  const W = 300;
  const H = 100;
  const pad = { top: 6, right: 6, bottom: 14, left: 24 };
  const plotW = W - pad.left - pad.right;
  const plotH = H - pad.top - pad.bottom;

  const xScale = (i: number) => pad.left + (i / (buckets.length - 1)) * plotW;
  const yScale = (v: number) => pad.top + plotH - (v / (maxY * 1.1)) * plotH;

  // Session colors
  const defaultColors = [
    'hsl(215, 65%, 58%)',
    'hsl(25, 75%, 52%)',
    'hsl(155, 48%, 52%)',
    'hsl(350, 65%, 50%)',
  ];
  const colors = sessionColorMap
    ? sessionArr.map((sid, i) =>
        sessionColorMap[sid] !== ''
          ? sessionColorMap[sid]
          : defaultColors[i % defaultColors.length]
      )
    : defaultColors;

  // Build stacked area paths + top line paths
  const areaPaths: {
    sid: string;
    areaPath: string;
    linePath: string;
    color: string;
  }[] = [];

  sessionArr.forEach((sid, si) => {
    const points: { x: number; y0: number; y1: number }[] = [];

    buckets.forEach((bucket, bi) => {
      let base = 0;

      sessionArr.slice(0, si).forEach((prevSid) => {
        base += bucket.sessions[prevSid];
      });

      const val = bucket.sessions[sid];

      points.push({ x: xScale(bi), y0: yScale(base), y1: yScale(base + val) });
    });

    // Top line path (for stroke outline)
    let linePath = `M ${points[0].x} ${points[0].y1}`;

    points.slice(1).forEach((pt, idx) => {
      const prev = points[idx];
      const cpx = (prev.x + pt.x) / 2;

      linePath += ` C ${cpx} ${prev.y1}, ${cpx} ${pt.y1}, ${pt.x} ${pt.y1}`;
    });

    // Full area path (fill)
    let areaPath = linePath;

    [...points].reverse().forEach((pt, revIdx) => {
      const origIdx = points.length - 1 - revIdx;

      if (origIdx === points.length - 1) {
        areaPath += ` L ${pt.x} ${pt.y0}`;
      } else {
        const next = points[origIdx + 1];
        const cpx = (next.x + pt.x) / 2;

        areaPath += ` C ${cpx} ${next.y0}, ${cpx} ${pt.y0}, ${pt.x} ${pt.y0}`;
      }
    });

    areaPath += ' Z';
    areaPaths.push({
      sid,
      areaPath,
      linePath,
      color: colors[si % colors.length],
    });
  });

  // Y ticks
  const yTicks = [0, Math.round(maxY / 2), maxY];

  return (
    <Box className='Sod-FindingDetail-miniChart'>
      <Typography kind='label' className='Sod-FindingDetail-miniChart-title'>
        {SESSION_ACTIVITY_PAREN}
        {buckets[0]?.label} {EN_DASH} {buckets[buckets.length - 1]?.label}
        {CLOSE_PAREN}
      </Typography>
      <div className='Sod-FindingDetail-miniChart-legend'>
        {sessionArr.map((sid, i) => (
          <span key={sid} className='Sod-FindingDetail-miniChart-legendItem'>
            <span
              className='Sod-FindingDetail-miniChart-legendDot'
              style={{ backgroundColor: colors[i % colors.length] }}
            />
            {S_PREFIX}
            {sid}
          </span>
        ))}
      </div>
      <svg
        className='Sod-FindingDetail-miniChart-svg'
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio='xMidYMid meet'>
        {/* Grid */}
        {yTicks.map((tick) => (
          <g key={`yt-${tick}`}>
            <line
              x1={pad.left}
              y1={yScale(tick)}
              x2={W - pad.right}
              y2={yScale(tick)}
              stroke='var(--color-border-neutral-decorative, #e5e7eb)'
              strokeWidth={0.5}
              strokeDasharray='3 2'
            />
            <text
              x={pad.left - 4}
              y={yScale(tick) + 3}
              fill='var(--color-foreground-neutral-subtle)'
              fontSize={7}
              textAnchor='end'>
              {tick}
            </text>
          </g>
        ))}

        {/* Areas */}
        {areaPaths.map((ap) => (
          <g key={ap.sid}>
            <path d={ap.areaPath} fill={ap.color} fillOpacity={0.5} />
            <path
              d={ap.linePath}
              fill='none'
              stroke={ap.color}
              strokeWidth={1}
              strokeOpacity={0.8}
            />
          </g>
        ))}

        {/* X labels */}
        {buckets.map((b, i) =>
          i % Math.max(1, Math.floor(buckets.length / 5)) === 0 ||
          i === buckets.length - 1 ? (
            <text
              key={`xl-${b.epoch}`}
              x={xScale(i)}
              y={H - 4}
              fill='var(--color-foreground-neutral-subtle)'
              fontSize={7}
              textAnchor='middle'>
              {b.label}
            </text>
          ) : null
        )}

        {/* Baseline */}
        <line
          x1={pad.left}
          y1={yScale(0)}
          x2={W - pad.right}
          y2={yScale(0)}
          stroke='var(--color-border-neutral-subtle)'
          strokeWidth={0.5}
        />

        {/* Step markers — dots on the chart at each finding step time */}
        {stepMarkers.map((marker) => {
          const range = maxTime - minTime;

          if (range <= 0) {
            return null;
          }

          const xPct = (marker.epoch - minTime) / range;
          const xPos = pad.left + xPct * plotW;

          // Find the stacked Y value at this position
          const bucketIdx = Math.min(
            buckets.length - 1,
            Math.max(0, Math.round(xPct * (buckets.length - 1)))
          );
          let stackedVal = 0;

          sessionArr.forEach((sid) => {
            stackedVal += buckets[bucketIdx]?.sessions[sid] ?? 0;
          });

          const yPos = yScale(stackedVal);

          return (
            <g key={`marker-${marker.epoch}-${marker.sessionId}`}>
              {/* Vertical drop line */}
              <line
                x1={xPos}
                y1={yPos}
                x2={xPos}
                y2={yScale(0)}
                stroke='var(--color-critical-700, #cc1c10)'
                strokeWidth={0.5}
                strokeDasharray='2 1'
                opacity={0.4}
              />
              {/* Outer ring */}
              <circle
                cx={xPos}
                cy={yPos}
                r={2.5}
                fill='var(--color-background-neutral-subtlest, #fff)'
                stroke='var(--color-critical-700, #cc1c10)'
                strokeWidth={1}
              />
              {/* Inner dot */}
              <circle
                cx={xPos}
                cy={yPos}
                r={1}
                fill='var(--color-critical-700, #cc1c10)'
              />
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

/**
 * Modal (or inline panel) showing finding sequence, evidence, and related metadata.
 */
function FindingDetailModal({
  finding,
  logEntries,
  onClose,
  isInline = false,
  sessionColorMap = null,
}: FindingDetailModalProps) {
  const l = useInvertedLocalize();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<ModalTab>('sequence');

  const sessionIds = useMemo(
    () => getSessionIdsForFinding(finding, logEntries),
    [finding, logEntries]
  );

  const sessionToRequestId = useMemo(() => {
    const map = new Map<string, string>();

    logEntries.forEach((entry) => {
      if (entry.sessionId && entry.requestId && !map.has(entry.sessionId)) {
        map.set(entry.sessionId, entry.requestId);
      }
    });

    return map;
  }, [logEntries]);

  const navigateToSession = useCallback(
    (sessionId: string) => {
      const requestId = sessionToRequestId.get(sessionId);

      if (requestId) {
        history.push(`/activitylog/${requestId}/view`);
      }
    },
    [sessionToRequestId, history]
  );

  const isCrossSession = sessionIds.length > 1;

  const evidenceEntries = useMemo(
    () => getLogEntriesByLines(logEntries, finding.evidenceLines),
    [logEntries, finding.evidenceLines]
  );

  const elapsed = useMemo(
    () => computeElapsedTime(finding.steps),
    [finding.steps]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const innerContent = (
    <>
      {/* Header — omitted in inline mode since the card already shows this info */}
      {!isInline && (
        <Box className='Sod-FindingDetail-header'>
          <Box className='Sod-FindingDetail-header-top'>
            <Box className='Sod-FindingDetail-header-badges'>
              <SeverityBadge severity={finding.severity} />
              <Box className='Sod-FindingCard-phaseTag'>
                <Typography
                  kind='label'
                  className='Sod-FindingCard-phaseTag-text'>
                  {getPhaseShortLabel(finding.phase)}
                </Typography>
              </Box>
              <Box
                className={cx(
                  'Sod-FindingCard-status',
                  `Sod-FindingCard-status--${finding.status}`
                )}>
                <Typography
                  kind='label'
                  className='Sod-FindingCard-status-text'>
                  {finding.status.charAt(0).toUpperCase() +
                    finding.status.slice(1)}
                </Typography>
              </Box>
              {isCrossSession && (
                <Box className='Sod-FindingDetail-crossBadge'>
                  <Typography
                    kind='label'
                    className='Sod-FindingDetail-crossBadge-text'>
                    {CROSS_SESSION}
                  </Typography>
                </Box>
              )}
            </Box>
            <button
              type='button'
              className='Sod-FindingDetail-closeBtn'
              onClick={onClose}
              aria-label={l('Close')}>
              {TIMES_SYMBOL}
            </button>
          </Box>
          <Typography kind='h3' className='Sod-FindingDetail-header-title'>
            {finding.title}
          </Typography>
          <Box className='Sod-FindingDetail-header-risk'>
            <RiskGauge score={finding.riskScore} />
            {elapsed !== '--' && (
              <Typography
                kind='label'
                className='Sod-FindingDetail-header-elapsed'>
                {ELAPSED_LABEL} {elapsed}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      {/* Tabs */}
      <Box className='Sod-FindingDetail-tabs'>
        <button
          type='button'
          className={cx('Sod-FindingDetail-tab', {
            'Sod-FindingDetail-tab--active': activeTab === 'sequence',
          })}
          onClick={() => setActiveTab('sequence')}>
          {SEQUENCE_TIMELINE}
        </button>
        <button
          type='button'
          className={cx('Sod-FindingDetail-tab', {
            'Sod-FindingDetail-tab--active': activeTab === 'evidence',
          })}
          onClick={() => setActiveTab('evidence')}>
          {EVIDENCE_LABEL}
        </button>
      </Box>

      {/* Tab Content */}
      <Box className='Sod-FindingDetail-body'>
        {activeTab === 'sequence' && (
          <Box className='Sod-FindingDetail-sequence'>
            {finding.steps.map((step, i) => (
              <StepCard
                key={`${step.action}-${step.timestamp ?? ''}`}
                step={step}
                isLast={i === finding.steps.length - 1}
                prevSessionId={
                  i > 0 ? finding.steps[i - 1].sessionId : undefined
                }
                onSessionClick={navigateToSession}
              />
            ))}
          </Box>
        )}

        {activeTab === 'evidence' && (
          <Box className='Sod-FindingDetail-evidence'>
            {evidenceEntries.length === 0 ? (
              <Typography
                kind='body1'
                className='Sod-FindingDetail-evidence-empty'>
                {NO_EVIDENCE_MSG}
              </Typography>
            ) : (
              <div className='Sod-FindingDetail-evidence-tableWrap'>
                <table
                  className='Sod-FindingDetail-evidence-table'
                  aria-label={l('Evidence log entries')}>
                  <thead>
                    <tr>
                      <th
                        className='Sod-FindingDetail-evidence-th Sod-FindingDetail-evidence-th--num'
                        scope='col'>
                        {HASH_PREFIX}
                      </th>
                      <th className='Sod-FindingDetail-evidence-th' scope='col'>
                        {SESSION_TH}
                      </th>
                      <th className='Sod-FindingDetail-evidence-th' scope='col'>
                        {TCODE_TH}
                      </th>
                      <th className='Sod-FindingDetail-evidence-th' scope='col'>
                        {LOG_TYPE_TH}
                      </th>
                      <th className='Sod-FindingDetail-evidence-th' scope='col'>
                        {START_UTC_TH}
                      </th>
                      <th className='Sod-FindingDetail-evidence-th' scope='col'>
                        {END_UTC_TH}
                      </th>
                      <th className='Sod-FindingDetail-evidence-th' scope='col'>
                        {PROGRAM_TH}
                      </th>
                      <th
                        className='Sod-FindingDetail-evidence-th Sod-FindingDetail-evidence-th--wide'
                        scope='col'>
                        {DETAILS_TH}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {evidenceEntries.map((entry) => {
                      const isHighlighted = finding.evidenceLines.includes(
                        entry.lineNumber
                      );

                      return (
                        <tr
                          key={entry.logId}
                          className={cx('Sod-FindingDetail-evidence-row', {
                            'Sod-FindingDetail-evidence-row--highlighted':
                              isHighlighted,
                          })}>
                          <td className='Sod-FindingDetail-evidence-td Sod-FindingDetail-evidence-td--num'>
                            {isHighlighted && (
                              <span
                                className='Sod-FindingDetail-evidence-flagDot'
                                aria-label={l('Key evidence')}
                              />
                            )}
                            {entry.lineNumber}
                          </td>
                          <td className='Sod-FindingDetail-evidence-td Sod-FindingDetail-evidence-td--mono'>
                            {entry.sessionId}
                          </td>
                          <td className='Sod-FindingDetail-evidence-td'>
                            {entry.transactionCode ? (
                              <span className='Sod-FindingDetail-evidence-tcode'>
                                {entry.transactionCode}
                              </span>
                            ) : (
                              <span className='Sod-FindingDetail-evidence-empty-cell'>
                                {EM_DASH}
                              </span>
                            )}
                          </td>
                          <td className='Sod-FindingDetail-evidence-td'>
                            {entry.logType || (
                              <span className='Sod-FindingDetail-evidence-empty-cell'>
                                {EM_DASH}
                              </span>
                            )}
                          </td>
                          <td className='Sod-FindingDetail-evidence-td Sod-FindingDetail-evidence-td--mono'>
                            {formatTimestamp(entry.startTimeUtc)}
                          </td>
                          <td className='Sod-FindingDetail-evidence-td Sod-FindingDetail-evidence-td--mono'>
                            {formatTimestamp(entry.endTimeUtc)}
                          </td>
                          <td className='Sod-FindingDetail-evidence-td Sod-FindingDetail-evidence-td--mono'>
                            {entry.programName || (
                              <span className='Sod-FindingDetail-evidence-empty-cell'>
                                {EM_DASH}
                              </span>
                            )}
                          </td>
                          <td className='Sod-FindingDetail-evidence-td Sod-FindingDetail-evidence-td--wide'>
                            {entry.details}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Box>
        )}
      </Box>

      {/* Impact & Recommendation — side by side */}
      <Box className='Sod-FindingDetail-footer'>
        <Box className='Sod-FindingDetail-infoRow'>
          <Box className='Sod-FindingDetail-infoBox Sod-FindingDetail-infoBox--impact'>
            <Typography
              kind='label'
              className='Sod-FindingDetail-infoBox-label'>
              {IMPACT_LABEL}
            </Typography>
            <Typography kind='body1' className='Sod-FindingDetail-infoBox-text'>
              {finding.impact}
            </Typography>
          </Box>
          <Box className='Sod-FindingDetail-infoBox Sod-FindingDetail-infoBox--recommendation'>
            <Typography
              kind='label'
              className='Sod-FindingDetail-infoBox-label'>
              {RECOMMENDATION_LABEL}
            </Typography>
            <Typography kind='body1' className='Sod-FindingDetail-infoBox-text'>
              {finding.recommendation}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );

  if (isInline) {
    return (
      <Box className='Sod-FindingDetail Sod-FindingDetail--inline'>
        <Box className='Sod-FindingDetail--inline-topRow'>
          <div className='Sod-FindingCard-miniChart'>
            <MiniTimelineChart
              finding={finding}
              logEntries={logEntries}
              sessionColorMap={sessionColorMap}
            />
          </div>
          <Box className='Sod-FindingDetail-infoBox Sod-FindingDetail-infoBox--impact'>
            <Typography
              kind='label'
              className='Sod-FindingDetail-infoBox-label'>
              {IMPACT_LABEL}
            </Typography>
            <Typography kind='body1' className='Sod-FindingDetail-infoBox-text'>
              {finding.impact}
            </Typography>
          </Box>
          <Box className='Sod-FindingDetail-infoBox Sod-FindingDetail-infoBox--recommendation'>
            <Typography
              kind='label'
              className='Sod-FindingDetail-infoBox-label'>
              {RECOMMENDATION_LABEL}
            </Typography>
            <Typography kind='body1' className='Sod-FindingDetail-infoBox-text'>
              {finding.recommendation}
            </Typography>
          </Box>
        </Box>
        {innerContent}
      </Box>
    );
  }

  return (
    <div className='Sod-FindingDetail-overlay' role='presentation'>
      <button
        type='button'
        className='Sod-FindingDetail-overlayBackdrop'
        aria-label={l('Close finding details')}
        onClick={onClose}
      />
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dialog shell; role=dialog + Escape; native <dialog> would require focus API refactor */}
      <div
        className='Sod-FindingDetail'
        role='dialog'
        aria-modal
        aria-label={l('Finding details')}
        onKeyDown={handleKeyDown}
        tabIndex={-1}>
        {innerContent}
      </div>
    </div>
  );
}

FindingDetailModal.defaultProps = defaultProps;

export default FindingDetailModal;
export { MiniTimelineChart };
